import * as React from 'react';
import moment from 'moment';
import propz from 'propz';
import classNames from 'classnames';
import { AppUser } from 'Src/views/App/App';
import { ActionItem, ActionsButton } from 'Src/components/ActionsButton/ActionsButton';
import { SchoolEvent, Participant } from 'Src/models/event';
import {
  getPitchNameFromTournamentByPitchId,
  getSchoolEventLocation,
  getTournamentGroupNameByGroupId
} from 'Src/helpers/event/schoolEventViewHelper';
import { isClubEvent, isMultipartyEvent, isNonTeamSportEvent } from 'Src/helpers/event/event';
import { getEventGeneratedName, getAgesGroupFormatted, getEventStatus } from 'Src/helpers/accessor/accessor';
import { SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/common';
import { EVENT_ACTION_BUTTONS_MAPPING, EVENT_STATUS_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/event';
import { EventHeadToHeadFixture, hasEventResults } from 'Src/components/EventHeadToHeadFixture/EventHeadToHeadFixture';
import { EventChangeNotices } from 'Src/components/EventChangeNotices/EventChangeNotices';
import './SchoolEventMainInformation.scss';
import { formatTournamentAges } from 'Src/helpers/ageGroups';
import { EventMatchResultSummary } from 'Src/components/EventMatchResultSummary/EventMatchResultSummary';

interface Props {
  user: AppUser;
  event: SchoolEvent;
  participants?: Participant[];
  actionItems?: ActionItem[];
  isPrevButtonDisabled?: boolean;
  isNextButtonDisabled?: boolean;
  onPrevEventClick?: () => void;
  onNextEventClick?: () => void;
  onViewMapClick?: () => void;
}

const Icon = ({ type }: { type: 'calendar' | 'clock' | 'pin' | 'sport' | 'left' | 'right' }) => {
  const paths = {
    calendar: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
    clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 7v5l3 2',
    pin: 'M12 22s7-6.13 7-12a7 7 0 1 0-14 0c0 5.87 7 12 7 12zM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
    sport: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z',
    left: 'M19 12H5M11 18l-6-6 6-6',
    right: 'M5 12h14M13 6l6 6-6 6'
  };
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[type]} />
    </svg>
  );
};

const ActionIcon = ({ type }: { type: 'notify' | 'edit' | 'print' | 'consent' }) => {
  const paths = {
    notify: 'M22 2 11 13M22 2l-7 20-4-9-9-4z',
    edit: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z',
    print: 'M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6z',
    consent: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11'
  };

  return (
    <svg className="eEventHero__actionIcon" viewBox="0 0 24 24" aria-hidden="true">
      <path d={paths[type]} />
    </svg>
  );
};

function findAction(items: ActionItem[], text: string): ActionItem | undefined {
  for (const item of items) {
    if (item.itemText === text) return item;
    const nested = item.subItems ? findAction(item.subItems, text) : undefined;
    if (nested) return nested;
  }
  return undefined;
}

function removeActions(items: ActionItem[], texts: string[]): ActionItem[] {
  return items
    .filter(item => !texts.includes(item.itemText))
    .map(item => {
      if (!item.subItems) return item;
      return { ...item, subItems: removeActions(item.subItems, texts) };
    })
    .filter(item => !item.subItems || item.subItems.length > 0);
}

function flattenActions(items: ActionItem[]): ActionItem[] {
  return items.reduce<ActionItem[]>((result, item) => {
    const { subItems, ...action } = item;

    if (action.onItemClick) {
      result.push(action);
    }

    if (subItems && subItems.length > 0) {
      result.push(...flattenActions(subItems));
    }

    return result;
  }, []);
}

function getActionIconType(itemText: string): string {
  switch (itemText) {
    case EVENT_ACTION_BUTTONS_MAPPING.DUPLICATE_EVENT:
    case EVENT_ACTION_BUTTONS_MAPPING.CLONE_TOURNAMENT_EVENT:
      return 'duplicate';
    case EVENT_ACTION_BUTTONS_MAPPING.ADD_SCHOOL:
    case EVENT_ACTION_BUTTONS_MAPPING.ADD_TEAM:
      return 'add';
    case EVENT_ACTION_BUTTONS_MAPPING.ADD_RESULTS:
    case EVENT_ACTION_BUTTONS_MAPPING.UPDATE_RESULTS:
      return 'results';
    case EVENT_ACTION_BUTTONS_MAPPING.UPDATE_POINTS:
      return 'points';
    case EVENT_ACTION_BUTTONS_MAPPING.SEND_CONSENT_REQUEST:
      return 'consent';
    case EVENT_ACTION_BUTTONS_MAPPING.CANCEL_EVENT:
    case EVENT_ACTION_BUTTONS_MAPPING.CANCEL_EVENT_AND_EDIT_NOTIFICATION_LIST:
      return 'cancel';
    case EVENT_ACTION_BUTTONS_MAPPING.UNCANCEL_EVENT:
      return 'restore';
    case EVENT_ACTION_BUTTONS_MAPPING.REPORT_AVAILABILITY:
      return 'availability';
    case EVENT_ACTION_BUTTONS_MAPPING.DOWNLOAD_CSV:
      return 'csv';
    case EVENT_ACTION_BUTTONS_MAPPING.DELETE_EVENT:
    case EVENT_ACTION_BUTTONS_MAPPING.CLEAN_SCHOOL_LIST:
    case EVENT_ACTION_BUTTONS_MAPPING.REMOVE_SCHOOLS:
      return 'delete';
    case EVENT_ACTION_BUTTONS_MAPPING.IMPORT_RESULTS_FROM_QUANTUM:
    case EVENT_ACTION_BUTTONS_MAPPING.IMPORT_RESULTS_FROM_MACSHA_ONE_4_ALL:
    case EVENT_ACTION_BUTTONS_MAPPING.IMPORT_RESULTS:
    case EVENT_ACTION_BUTTONS_MAPPING.IMPORT_RESULTS_FROM_QUANTUM_AQ:
      return 'import';
    case EVENT_ACTION_BUTTONS_MAPPING.EXPORT_START_LIST_TO_MACSHA_ONE_4_ALL:
      return 'export';
    case EVENT_ACTION_BUTTONS_MAPPING.ARBITER_BOARD:
      return 'board';
    case EVENT_ACTION_BUTTONS_MAPPING.ADD_VIDEO:
      return 'video';
    case EVENT_ACTION_BUTTONS_MAPPING.EXTENDED_PARTICIPANT_LIST:
    case EVENT_ACTION_BUTTONS_MAPPING.EXTENDED_TEAM_SHEET:
    case EVENT_ACTION_BUTTONS_MAPPING.EVENT_CONSENT_REQUESTS:
    case EVENT_ACTION_BUTTONS_MAPPING.EVENT_AVAILABILITY_REPORTS:
      return 'report';
    case EVENT_ACTION_BUTTONS_MAPPING.SEND_EVENT_NOTIFICATION:
    case EVENT_ACTION_BUTTONS_MAPPING.SEND_EVENT_NOTIFICATION_ELIGIBLE_STUDENTS:
      return 'notification';
    case EVENT_ACTION_BUTTONS_MAPPING.EDIT_EVENT_SETTINGS:
      return 'edit';
    default:
      return 'more';
  }
}

function isDangerAction(itemText: string): boolean {
  return (
    itemText === EVENT_ACTION_BUTTONS_MAPPING.DELETE_EVENT ||
    itemText === EVENT_ACTION_BUTTONS_MAPPING.CANCEL_EVENT ||
    itemText === EVENT_ACTION_BUTTONS_MAPPING.CANCEL_EVENT_AND_EDIT_NOTIFICATION_LIST
  );
}

export function SchoolEventMainInformation(props: Props) {
  const {
    event,
    user,
    participants = [],
    actionItems = [],
    isPrevButtonDisabled,
    isNextButtonDisabled,
    onPrevEventClick,
    onNextEventClick,
    onViewMapClick
  } = props;
  const { tournamentGroupId, quantumEventNumber, pitchId, startTime, endTime, sport, gender, tournament } = event;
  const tournamentName = propz.get(tournament, ['name']);
  const tournamentCustomGroups = propz.get(tournament, ['customGroups'], []);
  const tournamentGroupName = getTournamentGroupNameByGroupId(tournamentGroupId, tournamentCustomGroups);
  const pitchName = getPitchNameFromTournamentByPitchId(tournament || {}, pitchId);
  const eventAges = tournament ? formatTournamentAges(event.ages) : getAgesGroupFormatted(event, { user });
  const eventLocation = getSchoolEventLocation(event, user);
  const eventSpecificLocation = propz.get(event, ['location'], '');
  const eventOfficialName = getEventGeneratedName(event, { user });
  const eventGender = SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[gender];
  const eventStatus = getEventStatus(event);
  const replyToEmail = propz.get(event, ['replyToEmail'], '');
  const coordinates = propz.get(event, ['venue', 'postcodeData', 'point', 'coordinates']);
  const isCoordinatesExist = Array.isArray(coordinates) && coordinates.length === 2;
  const isIndividual = isNonTeamSportEvent(event);
  const isMultiparty = isMultipartyEvent(event) || participants.length > 2;
  const isHeadToHead = !isIndividual && !isMultiparty && participants.length === 2;
  const resultsExist = hasEventResults(event);
  const isFinished = eventStatus === EVENT_STATUS_SERVER_TO_CLIENT_MAPPING.FINISHED;
  const isCancelled = eventStatus === EVENT_STATUS_SERVER_TO_CLIENT_MAPPING.CANCELED;
  const reasonForChange = String(propz.get(event, ['reasonForChange'], '') || '').trim();
  const reasonForCancellation = String(propz.get(event, ['reasonForCancellation'], '') || '').trim();

  if (typeof props.actionItems === 'undefined') {
    const legacyStatusClasses = classNames({
      fontWeightBold: true,
      eCancelledStatusEvent: isCancelled
    });
    return (
      <div>
        <div className="mt-3 h4 font-weight-bold">{eventOfficialName}</div>
        <div className={`${legacyStatusClasses} font-weight-bold`}>{eventStatus}</div>
        <div>{sport.name}</div>
        <div>{`Start time: ${moment(startTime).format('D MMMM YYYY')}   ${moment(startTime).format('HH:mm')}`}</div>
        {endTime && (
          <div>{`Finish/Collection time: ${moment(endTime).format('D MMMM YYYY')}    ${moment(endTime).format(
            'HH:mm'
          )}`}</div>
        )}
        {tournamentName && <div>{`Tournament name: ${tournamentName}`}</div>}
        {quantumEventNumber && <div>{`Quantum number: ${quantumEventNumber}`}</div>}
        {tournamentGroupName && <div>{`Group: ${tournamentGroupName}`}</div>}
        {pitchName && <div>{`Pitch: ${pitchName}`}</div>}
        <div>{`${eventGender} / ${eventAges}`}</div>
        <div className="d-flex">
          <div className="mr-3">{`${isClubEvent(event) ? 'Location' : 'Venue'}: ${eventLocation}`}</div>
          {isCoordinatesExist && (
            <div className="bViewMap" onClick={onViewMapClick}>
              <span>View map</span>
              <img src="/dist/images/icons/location.png" alt="Location icon" />
            </div>
          )}
        </div>
        {eventSpecificLocation && <div>{`Specific location: ${eventSpecificLocation}`}</div>}
        {replyToEmail && (
          <div>
            Reply-To Email: <a href={`mailto:${replyToEmail}`}>{replyToEmail}</a>
          </div>
        )}
      </div>
    );
  }

  const editAction = findAction(actionItems, EVENT_ACTION_BUTTONS_MAPPING.EDIT_EVENT_SETTINGS);
  const notifyAction = findAction(actionItems, EVENT_ACTION_BUTTONS_MAPPING.SEND_EVENT_NOTIFICATION);
  const printAction =
    findAction(actionItems, EVENT_ACTION_BUTTONS_MAPPING.PRINT) || findAction(actionItems, 'Print forms and Reports');
  const overflowActions = flattenActions(
    removeActions(actionItems, [
      EVENT_ACTION_BUTTONS_MAPPING.EDIT_EVENT_SETTINGS,
      EVENT_ACTION_BUTTONS_MAPPING.SEND_EVENT_NOTIFICATION,
      EVENT_ACTION_BUTTONS_MAPPING.PRINT,
      'Print forms and Reports'
    ])
  ).map(item => ({
    ...item,
    iconType: getActionIconType(item.itemText),
    isDanger: isDangerAction(item.itemText)
  }));

  const statusClasses = classNames('eEventHero__status', {
    'eEventHero__status--cancelled': isCancelled,
    'eEventHero__status--finished': isFinished
  });

  const summaryText = isIndividual
    ? `${propz.get(event, ['individualsData', 'length'], 0)} participants`
    : `${participants.length} participants`;

  return (
    <section className="eEventHero">
      <div className="eEventHero__top">
        <div className="eEventHero__identity">
          {isHeadToHead ? (
            <EventHeadToHeadFixture event={event} participants={participants} />
          ) : (
            <div className="eEventHero__summary">
              <span className="eEventHero__sportIcon">
                <Icon type="sport" />
              </span>
              <div>
                <h1 className="eEventHero__title">{eventOfficialName}</h1>
                <div className="eEventHero__summaryText">
                  {summaryText}
                  {resultsExist ? ' · Results available' : isFinished ? ' · No results yet' : ''}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="eEventHero__controls">
          <span className={statusClasses}>
            <span className="eEventHero__statusDot" />
            {eventStatus}
          </span>
          {notifyAction && (
            <button type="button" className="eEventHero__action" onClick={notifyAction.onItemClick}>
              <ActionIcon type="notify" />
              Notify
            </button>
          )}
          {printAction && (
            <button type="button" className="eEventHero__action" onClick={printAction.onItemClick}>
              <ActionIcon type="print" />
              Print
            </button>
          )}
          {editAction && (
            <button
              type="button"
              className="eEventHero__action eEventHero__action--primary"
              onClick={editAction.onItemClick}
            >
              <ActionIcon type="edit" />
              Edit
            </button>
          )}
          <ActionsButton
            actionItems={overflowActions}
            text="…"
            buttonClassName="btn eEventHero__more dropdown-toggle"
          />
        </div>
      </div>

      <div className="eEventHero__meta">
        <span className="eEventHero__metaNavigation">
          <button type="button" aria-label="Previous event" disabled={isPrevButtonDisabled} onClick={onPrevEventClick}>
            <Icon type="left" />
          </button>
          <button type="button" aria-label="Next event" disabled={isNextButtonDisabled} onClick={onNextEventClick}>
            <Icon type="right" />
          </button>
        </span>
        <span>
          <Icon type="calendar" />
          {moment(startTime).format('ddd D MMM YYYY')}
        </span>
        <span>
          <Icon type="clock" />
          {moment(startTime).format('HH:mm')}
          {endTime ? ` – ${moment(endTime).format('HH:mm')}` : ''}
        </span>
        <button
          type="button"
          className="eEventHero__location"
          disabled={!isCoordinatesExist}
          onClick={isCoordinatesExist ? onViewMapClick : undefined}
        >
          <Icon type="pin" />
          {isClubEvent(event) ? 'Location' : 'Venue'}: {eventLocation}
          {eventSpecificLocation ? ` · ${eventSpecificLocation}` : ''}
          {isCoordinatesExist ? ' · View map' : ''}
        </button>
        <span className="eEventHero__classification">
          <Icon type="sport" />
          {[sport.name, eventAges, eventGender].filter(Boolean).map(value => (
            <span className="eEventHero__classificationChip" key={String(value)}>
              {value}
            </span>
          ))}
        </span>
      </div>

      {(tournamentName || tournamentGroupName || quantumEventNumber || pitchName || replyToEmail) && (
        <div className="eEventHero__secondaryMeta">
          {tournamentName && (
            <span className="eEventHero__tournamentMeta">
              Tournament: <strong>{tournamentName}</strong>
            </span>
          )}
          {tournamentGroupName && (
            <span>
              Group: <strong>{tournamentGroupName}</strong>
            </span>
          )}
          {pitchName && (
            <span>
              Pitch: <strong>{pitchName}</strong>
            </span>
          )}
          {quantumEventNumber && (
            <span>
              Quantum: <strong>{quantumEventNumber}</strong>
            </span>
          )}
          {replyToEmail && (
            <span>
              Reply-To: <a href={`mailto:${replyToEmail}`}>{replyToEmail}</a>
            </span>
          )}
        </div>
      )}
      <EventChangeNotices
        latestChange={reasonForChange}
        cancellationReason={isCancelled ? reasonForCancellation : ''}
      />
      <EventMatchResultSummary event={event} />
    </section>
  );
}
